import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { BookOpen, Download } from 'lucide-react';

// A single-file, dependency-free renderer for the trainer PDP / Mock guide.
// Content mirrors /app/docs/trainer-pdp-mock-guide.md — keep both in sync.
const RAW_MARKDOWN = `# Trainer Guide: PDP, Mock Interview & Video/Transcript

**Audience:** Trainers · **Owner:** Training Manager · **Last updated:** Aug 2026

This one-pager explains how you (as a trainer) log three pieces of student progress that the placement team needs before a batch closes:

1. **PDP** — Personal Development Plan status per student (short free-text note)
2. **Mock Interview** — status of the mock: \`Completed\`, \`Scheduled\`, \`Not Done\`
3. **Video / Transcript** — a public URL (Google Drive, YouTube unlisted, etc.) hosting the mock recording and/or transcript

## Where to fill

You fill this on your own **Trainer Portal → My Report** page. Each batch card lists every enrolled student with inline editable columns for PDP, Mock Int. and Video / Transcript.

**Steps:**
1. Sign in to the Trainer Portal with your trainer credentials.
2. Click **My Report** in the left navigation.
3. Pick the correct sub-tab (Weekday / Weekend / Completed / Upcoming / On Hold) and click the batch card to expand it.
4. On each student row, click into the cell and type:
   - **PDP** — a short note (e.g., *"Level 2 complete, needs revision on Docker"*)
   - **Mock Int.** — one of \`Completed\` / \`Scheduled\` / \`Not Done\`
   - **Video / Transcript** — paste the URL (must start with \`https://\`)
5. Click outside the cell (or press Enter). The value is saved instantly — no separate Save button.
6. A link icon appears next to the Video / Transcript cell — click it to open the URL and verify it works.

## Important rules

- **Fields save independently.** Editing only Mock Int. today and only PDP next week keeps both saved.
- **Never leave the Video / Transcript blank if the mock happened** — that link is what placement uses with hiring partners.
- **Share the video securely.** Google Drive → *Anyone with the link → Viewer*. YouTube → upload as *Unlisted*.
- **Transcripts optional** — one link that has video + transcript is fine, or just the video URL.
- **CRM-only fields.** WhatsApp Group Link, Audit Trainer Name, and Notes of Auditor are pulled from Zoho CRM automatically — they are read-only on your side.

## When to fill

You'll get a nudge when the batch crosses **70% completion**. That's the sweet spot. You can fill earlier for students who are ahead. Missing entries re-nag every **7 days** until every student has all three fields.

## What managers see

Training Managers filter students by:
- **Missing PDP** · **Missing Mock** · **Missing Video/Transcript Link**
- **Running batches only** · **Completion ≥ X %**

If you leave fields empty in a batch that's ≥ 70% done, you'll show up on the weekly report.

## Change history

Every save is logged with your name and timestamp. Managers can view the log next to each student's row on their side.

## Trouble filling?

| Symptom | Fix |
|---|---|
| Cell won't turn into an input | Click directly on the cell text; refresh (\`Ctrl+R\`) if it still doesn't respond |
| Save doesn't stick | Wait for the "Saved" toast; if it says "Failed to save", check your internet and try again |
| Link icon doesn't appear | The URL must start with \`https://\` — save again with the full URL |
| Value resets after refresh | Rare — ping your Training Manager immediately (bug) |

For anything else, escalate to the Training Manager who owns this doc.
`;

// Ultra-light markdown → JSX. Handles headings, paragraphs, ordered/unordered
// lists (with 1-level indented sub-bullets), bold **x**, italic *x*, inline
// code `x`, and pipe tables. Anything more exotic drops gracefully to plain
// text so we never crash.
function renderInline(text, keyPrefix = '') {
  // Match **bold**, *italic*, and `inline code`. Bold first so ** wins over *.
  const parts = [];
  let idx = 0;
  const regex = /(\*\*[^*]+\*\*|\*[^*\n]+\*|`[^`]+`)/g;
  let m;
  let k = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > idx) parts.push(text.slice(idx, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) {
      parts.push(<strong key={`${keyPrefix}-b-${k++}`} className="text-slate-900 font-semibold">{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('*')) {
      parts.push(<em key={`${keyPrefix}-i-${k++}`} className="text-slate-600 italic">{tok.slice(1, -1)}</em>);
    } else {
      parts.push(<code key={`${keyPrefix}-c-${k++}`} className="text-[11px] bg-slate-100 border border-slate-200 rounded px-1 py-0.5 text-violet-700 font-mono">{tok.slice(1, -1)}</code>);
    }
    idx = m.index + tok.length;
  }
  if (idx < text.length) parts.push(text.slice(idx));
  return parts;
}

function MarkdownLite({ src }) {
  const lines = src.split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    // Heading
    if (/^# /.test(line)) {
      blocks.push(<h1 key={i} className="text-lg font-bold text-slate-900 mb-2">{renderInline(line.replace(/^# /, ''), `h1-${i}`)}</h1>);
      i += 1; continue;
    }
    if (/^## /.test(line)) {
      blocks.push(<h2 key={i} className="text-sm font-semibold text-violet-700 mt-4 mb-1.5 uppercase tracking-wide">{renderInline(line.replace(/^## /, ''), `h2-${i}`)}</h2>);
      i += 1; continue;
    }
    // Table (very light detection)
    if (line.includes('|') && lines[i + 1] && /^\|?\s*-+/.test(lines[i + 1])) {
      const header = line.split('|').map(s => s.trim()).filter(Boolean);
      i += 2; // skip header + separator
      const rows = [];
      while (i < lines.length && lines[i].includes('|')) {
        rows.push(lines[i].split('|').map(s => s.trim()).filter(Boolean));
        i += 1;
      }
      blocks.push(
        <div key={`tbl-${i}`} className="my-2 overflow-x-auto">
          <table className="w-full text-xs border border-slate-200 rounded-md overflow-hidden">
            <thead className="bg-slate-50">
              <tr>{header.map((h, hi) => <th key={hi} className="text-left px-3 py-1.5 text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri} className="border-t border-slate-100">
                  {r.map((c, ci) => <td key={ci} className="px-3 py-1.5 text-slate-700 align-top">{renderInline(c, `td-${ri}-${ci}`)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }
    // Ordered list — supports indented "- " sub-bullets and numbering continuation
    if (/^\d+\.\s/.test(line)) {
      // Each item is: { text, sub: [strings] }
      const items = [];
      while (i < lines.length) {
        const cur = lines[i];
        if (/^\d+\.\s/.test(cur)) {
          items.push({ text: cur.replace(/^\d+\.\s/, ''), sub: [] });
          i += 1;
          // absorb any indented sub-bullets that belong to this step
          while (i < lines.length && /^\s{2,}-\s/.test(lines[i])) {
            items[items.length - 1].sub.push(lines[i].replace(/^\s{2,}-\s/, ''));
            i += 1;
          }
        } else if (cur.trim() === '' && i + 1 < lines.length && /^\d+\.\s/.test(lines[i + 1])) {
          // blank line between two numbered items — keep the list contiguous
          i += 1;
        } else {
          break;
        }
      }
      blocks.push(
        <ol key={`ol-${i}`} className="list-decimal list-outside pl-5 space-y-1 my-2 text-xs text-slate-700 leading-relaxed">
          {items.map((it, ii) => (
            <li key={ii}>
              {renderInline(it.text, `oli-${i}-${ii}`)}
              {it.sub.length > 0 && (
                <ul className="list-disc list-outside pl-5 mt-1 space-y-0.5">
                  {it.sub.map((sb, si) => <li key={si}>{renderInline(sb, `oli-${i}-${ii}-sub-${si}`)}</li>)}
                </ul>
              )}
            </li>
          ))}
        </ol>
      );
      continue;
    }
    // Unordered list
    if (/^-\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^-\s/.test(lines[i])) {
        items.push(lines[i].replace(/^-\s/, ''));
        i += 1;
      }
      blocks.push(
        <ul key={`ul-${i}`} className="list-disc list-outside pl-5 space-y-1 my-2 text-xs text-slate-700 leading-relaxed">
          {items.map((it, ii) => <li key={ii}>{renderInline(it, `uli-${i}-${ii}`)}</li>)}
        </ul>
      );
      continue;
    }
    // Blank
    if (line.trim() === '') { i += 1; continue; }
    // Paragraph
    blocks.push(<p key={i} className="text-xs text-slate-600 leading-relaxed my-1.5">{renderInline(line, `p-${i}`)}</p>);
    i += 1;
  }
  return <>{blocks}</>;
}

export default function TrainerGuideModal({ open, onOpenChange }) {
  const downloadMarkdown = () => {
    const blob = new Blob([RAW_MARKDOWN], { type: 'text/markdown;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'trainer-pdp-mock-guide.md';
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="trainer-guide-dialog">
        <DialogHeader className="border-b border-slate-100 pb-2">
          <DialogTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-violet-600" /> Trainer Guide · PDP, Mock & Video
          </DialogTitle>
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-slate-500">One-pager for logging PDP, Mock Interview and Video/Transcript per student.</p>
            <button onClick={downloadMarkdown}
              className="text-[10px] px-2 py-1 rounded border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 inline-flex items-center gap-1"
              data-testid="trainer-guide-download">
              <Download className="w-3 h-3" /> Download .md
            </button>
          </div>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto -mx-6 px-6 pt-2" data-testid="trainer-guide-body">
          <MarkdownLite src={RAW_MARKDOWN} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
